import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  reportAdminService,
  ReportRow,
  ReportStatus,
  ReportTargetType,
  ReportReasonCode,
} from '../services/reportAdminService';
import ReportReviewSidebar from '../components/ReportReviewSidebar';

// FEAT-041 — Admin Moderation Queue page (MVP-8.5/8.6/8.7).
// Filters by status / target type / reason / reporter / reportee.
// Row click opens the review sidebar with full context + audit-bearing
// transition controls. The page itself never surfaces any signal back to
// the reportee — it is purely an admin surface.

const STATUS_VALUES: Array<ReportStatus | 'ALL'> = [
  'ALL',
  'OPEN',
  'UNDER_REVIEW',
  'RESOLVED',
  'DISMISSED',
];

const TARGET_VALUES: Array<ReportTargetType | 'ALL'> = ['ALL', 'PROFILE', 'MESSAGE'];

const REASON_VALUES: Array<ReportReasonCode | 'ALL'> = [
  'ALL',
  'SPAM',
  'HARASSMENT',
  'UNSAFE_CONTENT',
  'MISREPRESENTED_CREDENTIALS',
  'OTHER',
];

const STATUS_COLOR: Record<ReportStatus, 'default' | 'warning' | 'success' | 'info'> = {
  OPEN: 'warning',
  UNDER_REVIEW: 'info',
  RESOLVED: 'success',
  DISMISSED: 'default',
};

const ModerationQueuePage: React.FC = () => {
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [status, setStatus] = useState<ReportStatus | 'ALL'>('OPEN');
  const [targetType, setTargetType] = useState<ReportTargetType | 'ALL'>('ALL');
  const [reasonCode, setReasonCode] = useState<ReportReasonCode | 'ALL'>('ALL');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await reportAdminService.listReports({
        status: status === 'ALL' ? undefined : status,
        targetType: targetType === 'ALL' ? undefined : targetType,
        reasonCode: reasonCode === 'ALL' ? undefined : reasonCode,
        page: page + 1,
        limit: rowsPerPage,
      });
      setReports(result.reports);
      setTotal(result.pagination.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load moderation queue');
    } finally {
      setLoading(false);
    }
  }, [status, targetType, reasonCode, page, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRowClick = (id: string) => {
    setSelectedId(id);
    setDrawerOpen(true);
  };

  const handleUpdated = (updated: ReportRow) => {
    setReports((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" data-testid="moderation-queue-title">
            Moderation Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reports submitted by users. Reportees are not notified — handle here.
          </Typography>
        </Box>
        <IconButton onClick={fetchData} aria-label="Refresh queue" data-testid="moderation-refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="filter-status-label">Status</InputLabel>
          <Select
            labelId="filter-status-label"
            label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as typeof status);
              setPage(0);
            }}
            inputProps={{ 'data-testid': 'filter-status' }}
          >
            {STATUS_VALUES.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="filter-target-label">Type</InputLabel>
          <Select
            labelId="filter-target-label"
            label="Type"
            value={targetType}
            onChange={(e) => {
              setTargetType(e.target.value as typeof targetType);
              setPage(0);
            }}
            inputProps={{ 'data-testid': 'filter-target' }}
          >
            {TARGET_VALUES.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="filter-reason-label">Reason</InputLabel>
          <Select
            labelId="filter-reason-label"
            label="Reason"
            value={reasonCode}
            onChange={(e) => {
              setReasonCode(e.target.value as typeof reasonCode);
              setPage(0);
            }}
            inputProps={{ 'data-testid': 'filter-reason' }}
          >
            {REASON_VALUES.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="moderation-queue-error">
          {error}
        </Alert>
      ) : null}

      <Paper>
        <TableContainer>
          <Table size="small" data-testid="moderation-queue-table">
            <TableHead>
              <TableRow>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Reporter</TableCell>
                <TableCell>Target snapshot</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No reports match these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => {
                  const snapshot = report.targetSnapshot as Record<string, unknown> | null;
                  const snapshotLabel =
                    report.targetType === 'PROFILE'
                      ? (snapshot?.displayName as string | undefined) || report.targetId
                      : ((snapshot?.excerpt as string | undefined) || '').slice(0, 80);
                  return (
                    <TableRow
                      key={report.id}
                      hover
                      onClick={() => handleRowClick(report.id)}
                      sx={{ cursor: 'pointer' }}
                      data-testid={`moderation-row-${report.id}`}
                    >
                      <TableCell>{new Date(report.createdAt).toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip
                          label={report.status}
                          size="small"
                          color={STATUS_COLOR[report.status]}
                        />
                      </TableCell>
                      <TableCell>{report.targetType}</TableCell>
                      <TableCell>{report.reasonCode}</TableCell>
                      <TableCell>
                        {report.reporter
                          ? `${report.reporter.firstName ?? ''} ${report.reporter.lastName ?? ''}`.trim() ||
                            report.reporter.email
                          : report.reporterId}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {snapshotLabel}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, value) => setPage(value)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>

      <ReportReviewSidebar
        reportId={selectedId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onUpdated={handleUpdated}
      />
    </Container>
  );
};

export default ModerationQueuePage;
